/**
 * Audio Upload Client for Life Guide
 * 
 * Handles uploading native audio recordings from web, iOS, and Android
 * applications to the Life Guide backend with direct GCS upload.
 * 
 * @packageDocumentation
 */

export interface AudioMetadata {
  /** Platform where audio was recorded */
  platform: 'web' | 'ios' | 'android';
  /** Audio file format */
  format: 'webm' | 'm4a' | 'mp4' | 'ogg' | 'wav';
  /** Recording duration in seconds */
  duration: number;
  /** Audio sample rate in Hz */
  sampleRate: number;
  /** Timestamp when recording started (ISO 8601) */
  recordedAt: string;
  /** Optional device information */
  deviceInfo?: DeviceInfo;
}

export interface DeviceInfo {
  /** Device model (e.g., "iPhone 15", "Pixel 8") */
  device?: string;
  /** Browser name (web only) */
  browser?: string;
  /** Browser version (web only) */
  browserVersion?: string;
  /** Operating system */
  os?: string;
  /** OS version */
  osVersion?: string;
}

export interface UploadSession {
  /** Audio recording unique identifier */
  audioId: string;
  /** Upload session identifier */
  sessionId: string;
  /** GCS signed URL for direct upload (expires in 1 hour) */
  signedUrl: string;
  /** Expiration timestamp of signed URL */
  expiresAt: string;
  /** Upload instructions */
  uploadInstructions?: {
    method: 'PUT';
    headers?: Record<string, string>;
  };
}

export interface AudioUploadResult {
  /** Audio recording unique identifier */
  audioId: string;
  /** Current processing status */
  status: 'processing';
  /** Timestamp when recording was created */
  createdAt: string;
  /** Estimated time until transcription ready (seconds) */
  estimatedProcessingTime?: number;
}

export interface UploadOptions {
  /** Progress callback (0-100) */
  onProgress?: (percent: number) => void;
  /** Abort controller for canceling upload */
  abortController?: AbortController;
  /** Retry attempts (default: 3) */
  retryAttempts?: number;
  /** Retry delay in ms (default: 1000) */
  retryDelay?: number;
}

export interface AudioUploadError extends Error {
  code: string;
  statusCode?: number;
  details?: any;
}

/**
 * Client for uploading audio recordings to Life Guide
 * 
 * @example
 * ```typescript
 * const client = new AudioUploadClient(
 *   'https://api.lifeguide.app/v1',
 *   async () => {
 *     const user = firebase.auth().currentUser;
 *     return user ? await user.getIdToken() : '';
 *   }
 * );
 * 
 * const result = await client.upload(audioBlob, {
 *   platform: 'web',
 *   format: 'webm',
 *   duration: 180.5,
 *   sampleRate: 48000,
 *   recordedAt: new Date().toISOString()
 * }, {
 *   onProgress: (percent) => console.log(`Upload ${percent}% complete`)
 * });
 * 
 * console.log('Audio ID:', result.audioId);
 * ```
 */
export class AudioUploadClient {
  private apiBaseUrl: string;
  private getAuthToken: () => Promise<string>;

  /**
   * Create a new AudioUploadClient
   * 
   * @param apiBaseUrl - Base URL for the API (e.g., 'https://api.lifeguide.app/v1')
   * @param getAuthToken - Function that returns a Firebase Auth token
   */
  constructor(apiBaseUrl: string, getAuthToken: () => Promise<string>) {
    this.apiBaseUrl = apiBaseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.getAuthToken = getAuthToken;
  }

  /**
   * Initiate an audio upload session
   * 
   * @param metadata - Audio metadata
   * @returns Upload session with signed GCS URL
   * @throws {AudioUploadError} If initiation fails
   */
  async initiateUpload(metadata: AudioMetadata): Promise<UploadSession> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${this.apiBaseUrl}/audio/upload/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(metadata),
    });

    if (!response.ok) {
      const error = await this.parseError(response);
      throw error;
    }

    return await response.json();
  }

  /**
   * Upload audio directly to GCS using signed URL
   * 
   * @param blob - Audio blob to upload
   * @param signedUrl - Signed GCS URL from initiate response
   * @param options - Upload options
   * @throws {AudioUploadError} If upload fails
   */
  async uploadDirect(
    blob: Blob,
    signedUrl: string,
    options?: UploadOptions
  ): Promise<void> {
    const { onProgress, abortController, retryAttempts = 3, retryDelay = 1000 } = options || {};

    let attempt = 0;
    let lastError: Error | null = null;

    while (attempt < retryAttempts) {
      try {
        await this.performUpload(blob, signedUrl, onProgress, abortController?.signal);
        return; // Success
      } catch (error) {
        lastError = error as Error;
        attempt++;
        
        if (attempt < retryAttempts) {
          console.warn(`Upload attempt ${attempt} failed, retrying in ${retryDelay}ms...`, error);
          await this.delay(retryDelay * attempt); // Exponential backoff
        }
      }
    }

    // All retries failed
    throw this.createError(
      'UPLOAD_FAILED',
      `Upload failed after ${retryAttempts} attempts: ${lastError?.message}`,
      undefined,
      { originalError: lastError }
    );
  }

  /**
   * Complete the upload session and trigger processing
   * 
   * @param sessionId - Upload session identifier
   * @param actualDuration - Actual duration of uploaded file (optional)
   * @param actualSize - Actual file size in bytes (optional)
   * @returns Upload result with audio ID
   * @throws {AudioUploadError} If completion fails
   */
  async completeUpload(
    sessionId: string,
    actualDuration?: number,
    actualSize?: number
  ): Promise<AudioUploadResult> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${this.apiBaseUrl}/audio/upload/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId,
        actualDuration,
        actualSize,
      }),
    });

    if (!response.ok) {
      const error = await this.parseError(response);
      throw error;
    }

    return await response.json();
  }

  /**
   * Upload audio recording (convenience method)
   * 
   * Combines initiate, upload, and complete steps into one call.
   * 
   * @param audioBlob - Audio blob to upload
   * @param metadata - Audio metadata
   * @param options - Upload options
   * @returns Upload result with audio ID
   * @throws {AudioUploadError} If any step fails
   */
  async upload(
    audioBlob: Blob,
    metadata: AudioMetadata,
    options?: UploadOptions
  ): Promise<AudioUploadResult> {
    // Step 1: Initiate upload
    const session = await this.initiateUpload(metadata);

    try {
      // Step 2: Upload to GCS
      await this.uploadDirect(audioBlob, session.signedUrl, options);

      // Step 3: Complete upload
      const result = await this.completeUpload(
        session.sessionId,
        metadata.duration,
        audioBlob.size
      );

      return result;
    } catch (error) {
      // If upload or completion fails, the session is abandoned
      // The backend will clean up expired sessions
      console.error('Upload failed:', error);
      throw error;
    }
  }

  /**
   * Perform the actual upload with progress tracking
   */
  private async performUpload(
    blob: Blob,
    signedUrl: string,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(this.createError(
            'UPLOAD_FAILED',
            `Upload failed with status ${xhr.status}`,
            xhr.status
          ));
        }
      });

      xhr.addEventListener('error', () => {
        reject(this.createError(
          'NETWORK_ERROR',
          'Network error during upload'
        ));
      });

      xhr.addEventListener('abort', () => {
        reject(this.createError(
          'UPLOAD_CANCELLED',
          'Upload was cancelled'
        ));
      });

      if (signal) {
        signal.addEventListener('abort', () => {
          xhr.abort();
        });
      }

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', blob.type);
      xhr.send(blob);
    });
  }

  /**
   * Parse API error response
   */
  private async parseError(response: Response): Promise<AudioUploadError> {
    try {
      const errorData = await response.json();
      return this.createError(
        errorData.code || 'UNKNOWN_ERROR',
        errorData.message || response.statusText,
        response.status,
        errorData.details
      );
    } catch {
      return this.createError(
        'UNKNOWN_ERROR',
        response.statusText || 'Unknown error',
        response.status
      );
    }
  }

  /**
   * Create an AudioUploadError
   */
  private createError(
    code: string,
    message: string,
    statusCode?: number,
    details?: any
  ): AudioUploadError {
    const error = new Error(message) as AudioUploadError;
    error.name = 'AudioUploadError';
    error.code = code;
    error.statusCode = statusCode;
    error.details = details;
    return error;
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Create device info for web platform
 */
export function getWebDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  
  // Simple browser detection (can be enhanced)
  let browser = 'Unknown';
  let browserVersion = '';
  
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : '';
  }

  // OS detection
  let os = 'Unknown';
  let osVersion = '';
  
  if (ua.includes('Win')) {
    os = 'Windows';
    if (ua.includes('Windows NT 10.0')) osVersion = '10/11';
  } else if (ua.includes('Mac')) {
    os = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    const match = ua.match(/OS (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (ua.includes('Android')) {
    os = 'Android';
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    osVersion = match ? match[1] : '';
  }

  return {
    browser,
    browserVersion,
    os,
    osVersion,
  };
}

